// Canonical Final UI renderer and visual authority for destination-transition skeletons.
const TUTOR_DESK_DESTINATION_SURFACE_ALIASES = Object.freeze({
  progress: 'assessment',
  library: 'digital-library',
  settings: 'settings',
  'platform.settings': 'settings',
  'student-progress': 'assessment',
});

const TUTOR_DESK_DESTINATION_SURFACES = new Set([
  'home',
  'students',
  'assessment',
  'students-portal',
  'calendar',
  'payments',
  'digital-library',
  'ai-quality',
  'settings',
  'inquiries',
]);

function escapeHtml(value = '') {
  return String(value ?? '').replace(/[<>&"']/gu, (character) => ({
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]));
}

function shape(className = '', attrs = '') {
  return `<span class="ttoria-destination-skeleton__shape${className ? ` ${className}` : ''}" aria-hidden="true"${attrs ? ` ${attrs}` : ''}></span>`;
}

function card(content = '', className = '') {
  return `<article class="ttoria-destination-skeleton__card${className ? ` ${className}` : ''}" aria-hidden="true">${content}</article>`;
}

function metricCards(count = 4) {
  return `<section class="ttoria-destination-skeleton__metrics ttoria-destination-skeleton__metrics--${Math.max(1, Math.min(8, count))}" aria-hidden="true">
    ${Array.from({ length: count }, (_, index) => card(`
      ${shape('ttoria-destination-skeleton__icon')}
      <span class="ttoria-destination-skeleton__metric-copy">
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--eyebrow')}
        ${shape(`ttoria-destination-skeleton__line ttoria-destination-skeleton__line--value ttoria-destination-skeleton__line--value-${(index % 3) + 1}`)}
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--support')}
      </span>
    `, 'ttoria-destination-skeleton__card--metric')).join('')}
  </section>`;
}

function toolbar({ fields = 2, compact = false } = {}) {
  return card(`
    <span class="ttoria-destination-skeleton__toolbar-fields">
      ${Array.from({ length: fields }, (_, index) => shape(`ttoria-destination-skeleton__field ttoria-destination-skeleton__field--${(index % 3) + 1}`)).join('')}
    </span>
    ${shape('ttoria-destination-skeleton__field ttoria-destination-skeleton__field--action')}
  `, `ttoria-destination-skeleton__card--toolbar${compact ? ' ttoria-destination-skeleton__card--toolbar-compact' : ''}`);
}

function table({ rows = 5, columns = 6 } = {}) {
  return card(`
    <span class="ttoria-destination-skeleton__table-heading">
      ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}
      ${shape('ttoria-destination-skeleton__chip')}
    </span>
    <span class="ttoria-destination-skeleton__table" data-columns="${columns}">
      <span class="ttoria-destination-skeleton__table-row ttoria-destination-skeleton__table-row--head">
        ${Array.from({ length: columns }, () => shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--cell')).join('')}
      </span>
      ${Array.from({ length: rows }, (_, rowIndex) => `<span class="ttoria-destination-skeleton__table-row">
        ${Array.from({ length: columns }, (_, columnIndex) => shape(`ttoria-destination-skeleton__line ttoria-destination-skeleton__line--cell ttoria-destination-skeleton__line--cell-${((rowIndex + columnIndex) % 3) + 1}`)).join('')}
      </span>`).join('')}
    </span>
  `, 'ttoria-destination-skeleton__card--table');
}

function workspacePanels(count = 2) {
  return `<section class="ttoria-destination-skeleton__workspace ttoria-destination-skeleton__workspace--${count}" aria-hidden="true">
    ${Array.from({ length: count }, (_, index) => card(`
      <span class="ttoria-destination-skeleton__panel-heading">
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}
        ${shape('ttoria-destination-skeleton__chip')}
      </span>
      <span class="ttoria-destination-skeleton__panel-body">
        ${shape(index % 2 === 0 ? 'ttoria-destination-skeleton__block ttoria-destination-skeleton__block--tall' : 'ttoria-destination-skeleton__block ttoria-destination-skeleton__block--medium')}
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--wide')}
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--medium')}
      </span>
    `, 'ttoria-destination-skeleton__card--workspace')).join('')}
  </section>`;
}

function renderHomeSkeleton() {
  return `
    <section class="ttoria-destination-skeleton__heading" aria-hidden="true">
      ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--display')}
      ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--subtitle')}
    </section>
    ${metricCards(8)}
    ${workspacePanels(2)}
  `;
}

function renderStudentsSkeleton() {
  return `
    ${metricCards(5)}
    ${toolbar({ fields: 1, compact: true })}
    ${table({ rows: 7, columns: 6 })}
  `;
}

function renderAssessmentSkeleton() {
  return `
    ${toolbar({ fields: 3 })}
    ${card(`${shape('ttoria-destination-skeleton__icon ttoria-destination-skeleton__icon--large')}
      <span class="ttoria-destination-skeleton__intro-copy">
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--wide')}
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--medium')}
      </span>`, 'ttoria-destination-skeleton__card--intro')}
    ${metricCards(8)}
    ${workspacePanels(2)}
  `;
}

function renderStudentsPortalSkeleton() {
  return `
    ${card(`${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--eyebrow')}${shape('ttoria-destination-skeleton__field ttoria-destination-skeleton__field--student')}`, 'ttoria-destination-skeleton__card--portal-selector')}
    ${card(`
      ${shape('ttoria-destination-skeleton__icon ttoria-destination-skeleton__icon--hero')}
      ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}
      ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--wide')}
      ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--medium')}
      <span class="ttoria-destination-skeleton__portal-summary">
        ${Array.from({ length: 3 }, (_, index) => `<span class="ttoria-destination-skeleton__portal-summary-card">
          ${shape(`ttoria-destination-skeleton__icon ttoria-destination-skeleton__icon--portal-${(index % 3) + 1}`)}
          <span class="ttoria-destination-skeleton__portal-summary-copy">
            ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--eyebrow')}
            ${shape(`ttoria-destination-skeleton__line ttoria-destination-skeleton__line--value ttoria-destination-skeleton__line--value-${(index % 3) + 1}`)}
          </span>
        </span>`).join('')}
      </span>
    `, 'ttoria-destination-skeleton__card--portal-workspace')}
  `;
}

function renderCalendarSkeleton() {
  return `
    ${toolbar({ fields: 3 })}
    ${metricCards(5)}
    ${card(`${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--wide')}${shape('ttoria-destination-skeleton__field ttoria-destination-skeleton__field--action')}`, 'ttoria-destination-skeleton__card--guidance')}
    <section class="ttoria-destination-skeleton__calendar-grid" aria-hidden="true">
      ${Array.from({ length: 7 }, (_, index) => card(`
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--day')}
        ${shape(`ttoria-destination-skeleton__block ttoria-destination-skeleton__block--day ttoria-destination-skeleton__block--day-${(index % 3) + 1}`)}
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--medium')}
      `, 'ttoria-destination-skeleton__card--day')).join('')}
    </section>
    ${table({ rows: 3, columns: 6 })}
  `;
}

function renderPaymentsSkeleton() {
  return `
    ${toolbar({ fields: 3 })}
    ${metricCards(3)}
    ${table({ rows: 6, columns: 6 })}
  `;
}

function renderLibrarySkeleton() {
  return `
    ${toolbar({ fields: 1 })}
    ${metricCards(4)}
    ${toolbar({ fields: 5, compact: true })}
    <section class="ttoria-destination-skeleton__library-workspace" aria-hidden="true">
      <span class="ttoria-destination-skeleton__resource-grid">
        ${Array.from({ length: 6 }, () => card(`
          ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}
          ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--wide')}
          ${shape('ttoria-destination-skeleton__chip')}
        `, 'ttoria-destination-skeleton__card--resource')).join('')}
      </span>
      ${card(`${shape('ttoria-destination-skeleton__icon ttoria-destination-skeleton__icon--hero')}${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--medium')}`, 'ttoria-destination-skeleton__card--preview')}
    </section>
  `;
}

function renderAiQualitySkeleton() {
  return `
    ${toolbar({ fields: 1, compact: true })}
    ${card(`${shape('ttoria-destination-skeleton__icon')}${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--wide')}${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--medium')}`, 'ttoria-destination-skeleton__card--notice')}
    ${metricCards(3)}
    ${workspacePanels(2)}
  `;
}

function renderSettingsSkeleton() {
  return `
    ${card(`${shape('ttoria-destination-skeleton__brand')}
      <span class="ttoria-destination-skeleton__intro-copy">
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--medium')}
      </span>`, 'ttoria-destination-skeleton__card--identity')}
    ${card(`${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}`, 'ttoria-destination-skeleton__card--section-label')}
    <section class="ttoria-destination-skeleton__theme-grid" aria-hidden="true">
      ${Array.from({ length: 2 }, () => card(`
        ${shape('ttoria-destination-skeleton__block ttoria-destination-skeleton__block--theme')}
        ${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}
        ${shape('ttoria-destination-skeleton__chip')}
      `, 'ttoria-destination-skeleton__card--theme')).join('')}
    </section>
    ${toolbar({ fields: 0, compact: true })}
  `;
}

function renderInquiriesSkeleton() {
  return `
    ${metricCards(4)}
    ${toolbar({ fields: 2 })}
    ${table({ rows: 6, columns: 5 })}
  `;
}

const SURFACE_RENDERERS = Object.freeze({
  home: renderHomeSkeleton,
  students: renderStudentsSkeleton,
  assessment: renderAssessmentSkeleton,
  'students-portal': renderStudentsPortalSkeleton,
  calendar: renderCalendarSkeleton,
  payments: renderPaymentsSkeleton,
  'digital-library': renderLibrarySkeleton,
  'ai-quality': renderAiQualitySkeleton,
  settings: renderSettingsSkeleton,
  inquiries: renderInquiriesSkeleton,
});

export function normalizeTutorDeskDestinationSurface(surface = '') {
  const normalized = String(surface || '').trim().toLowerCase();
  const resolved = TUTOR_DESK_DESTINATION_SURFACE_ALIASES[normalized] || normalized;
  return TUTOR_DESK_DESTINATION_SURFACES.has(resolved) ? resolved : '';
}

export function renderTutorDeskDestinationSkeleton({
  surface = '',
  title = '',
  detail = '',
  testId = 'tutor-desk-destination-skeleton',
  includeChrome = false,
  location = 'initial-data',
} = {}) {
  const normalizedSurface = normalizeTutorDeskDestinationSurface(surface) || 'home';
  const normalizedTitle = String(title || '').trim() || `Loading ${normalizedSurface}`;
  const normalizedDetail = String(detail || '').trim() || 'Preparing the selected panel.';
  const normalizedTestId = String(testId || '').trim() || 'tutor-desk-destination-skeleton';
  const renderSurface = SURFACE_RENDERERS[normalizedSurface] || renderHomeSkeleton;
  return `
    <article
      class="ttoria-destination-skeleton ttoria-destination-skeleton--${escapeHtml(normalizedSurface)}${includeChrome ? ' ttoria-destination-skeleton--with-chrome' : ''}"
      data-testid="${escapeHtml(normalizedTestId)}"
      data-kg-ui-role="destination-skeleton"
      data-kg-ui-state="loading"
      data-skeleton-surface="${escapeHtml(normalizedSurface)}"
      data-skeleton-location="${escapeHtml(location)}"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="${escapeHtml(`${normalizedTitle}. ${normalizedDetail}`)}"
    >
      ${includeChrome ? `<span class="ttoria-destination-skeleton__chrome" aria-hidden="true">${shape('ttoria-destination-skeleton__line ttoria-destination-skeleton__line--title')}${shape('ttoria-destination-skeleton__field ttoria-destination-skeleton__field--action')}</span>` : ''}
      <span class="ttoria-destination-skeleton__content" aria-hidden="true">
        ${renderSurface()}
      </span>
    </article>
  `;
}

export const TUTOR_DESK_SAAS_DESTINATION_SKELETON_STYLES = `
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 100%;
  padding: clamp(12px, 1.35vw, 22px);
  overflow: hidden;
  border: 1px solid var(--ttoria-final-border, #203a5c);
  border-radius: var(--ttoria-final-radius-card, 16px);
  background: var(--ttoria-final-app-bg, #020b14);
  color: var(--ttoria-final-text, #f7fbff);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton * {
  box-sizing: border-box;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__content {
  width: 100%;
  min-width: 0;
  display: grid;
  gap: clamp(10px, 1vw, 16px);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__shape {
  display: block;
  min-width: 0;
  border-radius: 8px;
  background: var(--ttoria-final-skeleton-shape, #172d4b);
  opacity: 0.86;
  animation: ttoriaDestinationSkeletonPulse 1.6s ease-in-out 1s infinite alternate;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card {
  min-width: 0;
  display: grid;
  gap: 10px;
  padding: clamp(12px, 1.1vw, 18px);
  overflow: hidden;
  border: 1px solid var(--ttoria-final-border, #203a5c);
  border-radius: var(--ttoria-final-radius-card, 16px);
  background: var(--ttoria-final-skeleton-surface, #0b1930);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__heading {
  display: grid;
  gap: 8px;
  padding: 2px 2px 4px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line { height: 10px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--display { width: min(34%, 310px); height: 26px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--subtitle { width: min(48%, 440px); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--eyebrow { width: 43%; height: 8px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--title { width: 52%; height: 13px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--value { height: 23px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--value-1 { width: 28%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--value-2 { width: 42%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--value-3 { width: 34%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--support { width: 76%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--wide { width: 88%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--medium { width: 64%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--day { width: 38%; height: 12px; }

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__metrics {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(8px, 0.8vw, 14px);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__metrics--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__metrics--5 { grid-template-columns: repeat(5, minmax(0, 1fr)); }

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--metric {
  min-height: 104px;
  grid-template-columns: 46px minmax(0, 1fr);
  align-items: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__icon--large { width: 58px; height: 58px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__icon--hero { width: 72px; height: 72px; border-radius: 50%; }

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] :where(.ttoria-destination-skeleton__metric-copy, .ttoria-destination-skeleton__intro-copy) {
  min-width: 0;
  display: grid;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__workspace {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(10px, 1vw, 16px);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--workspace { min-height: clamp(220px, 36vh, 440px); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__panel-heading,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__table-heading {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__panel-body { display: grid; gap: 12px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__chip { width: 72px; height: 24px; border-radius: 999px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__block { width: 100%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__block--tall { height: clamp(140px, 25vh, 300px); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__block--medium { height: clamp(110px, 20vh, 230px); }

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--toolbar {
  min-height: 64px;
  grid-template-columns: minmax(0, 1fr) minmax(112px, 18%);
  align-items: center;
}
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__toolbar-fields {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__field { height: 40px; border-radius: 10px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__field--action { width: 100%; max-width: 180px; justify-self: end; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__field--student { width: min(42%, 440px); }

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--intro,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--notice,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--identity {
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--portal-workspace {
  min-height: clamp(360px, 64vh, 680px);
  place-items: center;
  align-content: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__portal-summary {
  width: min(720px, 92%);
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__portal-summary-card {
  min-width: 0;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--ttoria-final-border, #203a5c);
  border-radius: 12px;
  background: color-mix(in srgb, var(--ttoria-final-skeleton-surface, #0b1930) 84%, #13294a 16%);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__portal-summary-card .ttoria-destination-skeleton__icon {
  width: 38px;
  height: 38px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__portal-summary-copy {
  min-width: 0;
  display: grid;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--guidance {
  min-height: 62px;
  grid-template-columns: minmax(0, 1fr) minmax(112px, 18%);
  align-items: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__calendar-grid {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: clamp(6px, 0.65vw, 10px);
}
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--day { min-height: clamp(118px, 18vh, 190px); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__block--day { height: 48px; }

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--table { min-height: 190px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__table { display: grid; min-width: 0; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__table-row {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(6, minmax(58px, 1fr));
  gap: 14px;
  padding: 13px 4px;
  border-top: 1px solid var(--ttoria-final-border, #203a5c);
}
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__table[data-columns="5"] .ttoria-destination-skeleton__table-row { grid-template-columns: repeat(5, minmax(58px, 1fr)); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__table-row--head { border-top: 0; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--cell { width: 78%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--cell-2 { width: 58%; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__line--cell-3 { width: 68%; }

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__library-workspace {
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(240px, 1fr);
  gap: clamp(10px, 1vw, 16px);
}
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__resource-grid {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--resource { min-height: 128px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--preview { min-height: 300px; place-items: center; align-content: center; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__brand { width: min(220px, 32vw); height: 68px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__theme-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__block--theme { height: clamp(180px, 28vh, 300px); }

@keyframes ttoriaDestinationSkeletonPulse {
  from { opacity: 0.66; }
  to { opacity: 0.96; }
}

@media (max-width: 1180px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__metrics,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__metrics--5 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__calendar-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

@media (max-width: 760px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton { padding: 10px; }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] :where(.ttoria-destination-skeleton__metrics, .ttoria-destination-skeleton__metrics--3, .ttoria-destination-skeleton__metrics--5, .ttoria-destination-skeleton__workspace, .ttoria-destination-skeleton__theme-grid, .ttoria-destination-skeleton__library-workspace) {
    grid-template-columns: minmax(0, 1fr);
  }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__calendar-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__resource-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--toolbar,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--guidance {
    grid-template-columns: minmax(0, 1fr);
  }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__field--action { justify-self: stretch; max-width: none; }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--portal-workspace { min-height: 320px; }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__portal-summary { grid-template-columns: minmax(0, 1fr); }
}

@media (max-width: 520px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__calendar-grid,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__resource-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__card--metric { grid-template-columns: 38px minmax(0, 1fr); min-height: 88px; }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__icon { width: 38px; height: 38px; }
}

@media (prefers-reduced-motion: reduce) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-async-feedback-standard="ttoria-saas-async-feedback-v1"][data-kg-async-feedback-app-id="tutor-desk"] .ttoria-destination-skeleton__shape { animation: none; }
}
`;
